import type { PaymentMethod } from '@barso/shared';
import type { CartLine, LocalSale } from './types';

/**
 * Korrigieren des letzten Verkaufs (K5): Der Verkauf wird storniert, seine noch gültigen Zeilen kommen in den
 * Warenkorb zurück, danach wird neu kassiert. Diese reinen Funktionen rechnen nur den **Hinweis** für das Bezahl-Blatt
 * aus. Gebucht wird nichts: Storno und neuer Verkauf laufen über die bisherigen Wege (`voidSale`, `checkout`).
 */

/** Zeilen eines Verkaufs, die der Kunde noch bezahlt hat (nicht einzeln storniert). */
export function correctableLines(sale: LocalSale): CartLine[] {
  return sale.lines.filter((line) => !line.voided).map(({ voided: _voided, ...line }) => line);
}

/** Bar bezahlter Betrag des Verkaufs nach Abzug schon einzeln stornierter Zeilen (ganze Cent). */
export function paidCents(sale: LocalSale): number {
  return sale.lines.reduce((sum, line) => sum + (line.voided ? 0 : line.priceCents), 0);
}

/** Der Vermerk „Korrektur zu Nr. 42“, solange der neue Warenkorb daraus entstanden ist. */
export interface Correction {
  registerSeq: number;
  /** Was der Kunde für den stornierten Verkauf schon bezahlt hat. */
  paidCents: number;
  /** Zahlart des korrigierten Verkaufs (U14); fehlt sie, war es bar. */
  payment?: PaymentMethod | undefined;
}

export type Settlement =
  { kind: 'pay'; cents: number } | { kind: 'refund'; cents: number } | { kind: 'even' };

/** Verrechnung nur als Hilfe: neue Summe gegen den schon bezahlten Betrag. */
export function settlementFor(newTotalCents: number, alreadyPaidCents: number): Settlement {
  const difference = newTotalCents - alreadyPaidCents;
  if (difference > 0) return { kind: 'pay', cents: difference };
  if (difference < 0) return { kind: 'refund', cents: -difference };
  return { kind: 'even' };
}

/** Text des Hinweises im Bezahl-Blatt. */
export function settlementText(
  correction: Correction,
  newTotalCents: number,
  format: (cents: number) => string,
): string {
  if (correction.payment === 'card') {
    return `Nr. ${String(correction.registerSeq)} wurde mit Karte bezahlt: neu mit „Karte“ kassieren und die Differenz am eigenen Kartengerät verrechnen.`;
  }
  const base = `Aus Nr. ${String(correction.registerSeq)} schon bezahlt: ${format(correction.paidCents)}`;
  const result = settlementFor(newTotalCents, correction.paidCents);
  if (result.kind === 'pay') return `${base} → Kunde zahlt noch ${format(result.cents)}`;
  if (result.kind === 'refund') return `${base} → Kunde bekommt ${format(result.cents)} zurück`;
  return `${base} → nichts mehr zu zahlen`;
}
